/**
 * Serializer for Attendance API to handle data transformation between frontend and Flask backend
 * Based on the Flask Attendance model and API endpoints
 */
class AttendanceSerializer {
  /**
   * Transform API response data to frontend format
   */
  fromJSON(apiData) {
    const attendance = apiData?.data || apiData;
    if (!attendance) {
      return null;
    }
    const frontendAttendance = {
      id: attendance.id,
      libraryId: attendance.library_id || "",
      userName: attendance.user_name || "",
      program: attendance.program || "",
      year: attendance.year || null,
      purposeOfVisit: attendance.purpose_of_visit || "",
      // Timestamps
      inTime: attendance.in_time
        ? this._formatDateTimeForFrontend(attendance.in_time)
        : null,
      outTime: attendance.out_time
        ? this._formatDateTimeForFrontend(attendance.out_time)
        : null,
      // Status derived from out_time
      status: attendance.out_time ? "completed" : "active",
      isActive: !attendance.out_time,
      // Duration calculations (if session is complete)
      duration: this._calculateDuration(
        attendance.in_time,
        attendance.out_time
      ),
      // Display formatted dates
      inTimeDisplay: attendance.in_time
        ? this._formatDateTimeForDisplay(attendance.in_time)
        : "",
      outTimeDisplay: attendance.out_time
        ? this._formatDateTimeForDisplay(attendance.out_time)
        : "",
      dateDisplay: attendance.in_time
        ? this._formatDateForDisplay(attendance.in_time)
        : "",
      // Metadata
      createdBy: attendance.created_by || "",
      updatedBy: attendance.updated_by || "",
      // Raw backend data for reference
      metadata: {
        created_by: attendance.created_by,
        updated_by: attendance.updated_by,
        raw_in_time: attendance.in_time,
        raw_out_time: attendance.out_time,
        session_duration: attendance.session_duration || null,
      },
    };
    return frontendAttendance;
  }
  /**
   * Transform frontend data to backend API format
   */
  toJSON(frontendData) {
    const backendAttendance = {
      library_id: String(
        frontendData.libraryId || frontendData.library_id || ""
      ).trim(),
      user_name: (frontendData.userName || frontendData.user_name || "").trim(),
      program: (frontendData.program || "").trim(),
      year: frontendData.year || null,
      purpose_of_visit: (
        frontendData.purposeOfVisit ||
        frontendData.purpose_of_visit ||
        ""
      ).trim(),
      created_by: frontendData.created_by || frontendData.createdBy,
      updated_by: frontendData.updated_by || frontendData.updatedBy,
    };
    // Handle manual timestamp updates (admin operations)
    if (frontendData.inTime && frontendData.inTime !== "") {
      backendAttendance.in_time = this._formatDateTimeForBackend(
        frontendData.inTime
      );
    }
    if (frontendData.outTime && frontendData.outTime !== "") {
      backendAttendance.out_time = this._formatDateTimeForBackend(
        frontendData.outTime
      );
    }
    // Remove null/undefined/empty values to avoid sending unnecessary data
    Object.keys(backendAttendance).forEach((key) => {
      if (
        backendAttendance[key] === null ||
        backendAttendance[key] === undefined ||
        backendAttendance[key] === ""
      ) {
        delete backendAttendance[key];
      }
    });
    return backendAttendance;
  }
  /**
   * Convert datetime string from backend to frontend format
   */
  _formatDateTimeForFrontend(backendDateTime) {
    if (!backendDateTime) return null;
    try {
      const date = new Date(backendDateTime);
      if (isNaN(date.getTime())) {
        return null;
      }
      return date.toISOString();
    } catch (error) {
      console.warn("DateTime formatting error:", error);
      return null;
    }
  }
  /**
   * Convert datetime from frontend to backend ISO format
   */
  _formatDateTimeForBackend(frontendDateTime) {
    if (!frontendDateTime) return null;
    try {
      const date = new Date(frontendDateTime);
      if (isNaN(date.getTime())) {
        return null;
      }
      return date.toISOString();
    } catch (error) {
      console.warn("DateTime formatting error:", error);
      return null;
    }
  }
  /**
   * Format datetime for display in UI
   */
  _formatDateTimeForDisplay(dateTimeString) {
    if (!dateTimeString) return "";
    try {
      const date = new Date(dateTimeString);
      if (isNaN(date.getTime())) {
        return "";
      }
      return date.toLocaleString("en-US", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: true,
      });
    } catch (error) {
      console.warn("DateTime display formatting error:", error);
      return "";
    }
  }
  /**
   * Format date for display (date only)
   */
  _formatDateForDisplay(dateTimeString) {
    if (!dateTimeString) return "";
    try {
      const date = new Date(dateTimeString);
      if (isNaN(date.getTime())) {
        return "";
      }
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    } catch (error) {
      console.warn("Date display formatting error:", error);
      return "";
    }
  }
  /**
   * Calculate session duration
   */
  _calculateDuration(inTime, outTime) {
    if (!inTime) return null;
    try {
      const startTime = new Date(inTime);
      const endTime = outTime ? new Date(outTime) : new Date();
      if (isNaN(startTime.getTime()) || (outTime && isNaN(endTime.getTime()))) {
        return null;
      }
      const durationMs = endTime - startTime;
      const durationHours = durationMs / (1000 * 60 * 60);
      const durationMinutes = durationMs / (1000 * 60);
      return {
        milliseconds: durationMs,
        minutes: Math.round(durationMinutes * 10) / 10,
        hours: Math.round(durationHours * 100) / 100,
        formatted: this._formatDuration(durationMs),
        isOngoing: !outTime,
      };
    } catch (error) {
      console.warn("Duration calculation error:", error);
      return null;
    }
  }
  /**
   * Format duration for display
   */
  _formatDuration(durationMs) {
    if (!durationMs || durationMs < 0) return "0 minutes";
    const hours = Math.floor(durationMs / (1000 * 60 * 60));
    const minutes = Math.floor((durationMs % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((durationMs % (1000 * 60)) / 1000);
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    } else if (minutes > 0) {
      return `${minutes}m ${seconds}s`;
    } else {
      return `${seconds}s`;
    }
  }
  /**
   * Transform search response format to standardized format
   */
  fromSearchJSON(searchResponse) {
    if (!searchResponse || !searchResponse.data) {
      return { hits: [], total: 0 };
    }
    const data = searchResponse.data;
    // Handle array of attendance records directly
    if (Array.isArray(data)) {
      return {
        hits: data.map((item) => this.fromJSON(item)),
        total: data.length,
      };
    }
    // Handle search results with hits array
    if (data.hits && Array.isArray(data.hits)) {
      return {
        hits: data.hits.map((item) => this.fromJSON(item)),
        total: data.total || data.hits.length,
      };
    }
    // Handle nested data structure
    if (data.data && Array.isArray(data.data)) {
      return {
        hits: data.data.map((item) => this.fromJSON(item)),
        total: data.total || data.data.length,
      };
    }
    // Handle single item response
    if (data.id) {
      return {
        hits: [this.fromJSON(data)],
        total: 1,
      };
    }
    return { hits: [], total: 0 };
  }
  /**
   * Validate attendance data before sending to backend
   */
  validateAttendanceData(data, operation = "check-in") {
    const errors = {};
    // Library ID validation
    if (!data.libraryId && !data.library_id) {
      errors.libraryId = "Library ID is required";
    } else {
      const libraryId = data.libraryId || data.library_id;
      if (String(libraryId).trim().length === 0) {
        errors.libraryId = "Library ID cannot be empty";
      } else if (String(libraryId).trim().length > 50) {
        errors.libraryId = "Library ID must be 50 characters or less";
      }
    }
    // User name validation
    if (!data.userName && !data.user_name) {
      errors.userName = "User name is required";
    } else {
      const userName = data.userName || data.user_name;
      if (String(userName).trim().length === 0) {
        errors.userName = "User name cannot be empty";
      } else if (String(userName).trim().length > 100) {
        errors.userName = "User name must be 100 characters or less";
      }
    }
    // Program validation (optional)
    if (data.program && String(data.program).trim().length > 100) {
      errors.program = "Program must be 100 characters or less";
    }
    // Purpose of visit validation (optional)
    if (
      data.purposeOfVisit &&
      String(data.purposeOfVisit).trim().length > 200
    ) {
      errors.purposeOfVisit = "Purpose of visit must be 200 characters or less";
    }
    // Year validation (optional)
    if (data.year !== null && data.year !== undefined) {
      const year = parseInt(data.year);
      if (isNaN(year) || year < 1900 || year > 2100) {
        errors.year = "Year must be a valid year between 1900 and 2100";
      }
    }
    // DateTime validations for manual entry/updates
    if (operation === "update") {
      if (data.inTime) {
        const inTime = new Date(data.inTime);
        if (isNaN(inTime.getTime())) {
          errors.inTime = "Invalid check-in time format";
        }
      }
      if (data.outTime) {
        const outTime = new Date(data.outTime);
        if (isNaN(outTime.getTime())) {
          errors.outTime = "Invalid check-out time format";
        }
        // Check that out time is after in time
        if (data.inTime && !errors.inTime && !errors.outTime) {
          const inTime = new Date(data.inTime);
          if (outTime <= inTime) {
            errors.outTime = "Check-out time must be after check-in time";
          }
        }
      }
    }
    return {
      isValid: Object.keys(errors).length === 0,
      errors,
    };
  }
  /**
   * Get status display information
   */
  getStatusDisplay(status) {
    const statusMap = {
      active: {
        label: "Checked In",
        color: "green",
        icon: "check-circle",
        description: "Currently in the library",
      },
      completed: {
        label: "Checked Out",
        color: "gray",
        icon: "clock",
        description: "Session completed",
      },
    };
    return statusMap[status] || statusMap.completed;
  }
  /**
   * Get all available programs/departments
   */
  getAvailablePrograms() {
    return [
      { value: "", label: "Not Specified" },
      { value: "Computer Science", label: "Computer Science" },
      { value: "Information Technology", label: "Information Technology" },
      { value: "Engineering", label: "Engineering" },
      { value: "Business Administration", label: "Business Administration" },
      { value: "Education", label: "Education" },
      { value: "Liberal Arts", label: "Liberal Arts" },
      { value: "Sciences", label: "Sciences" },
      { value: "Other", label: "Other" },
    ];
  }
  /**
   * Get available purposes of visit
   */
  getAvailablePurposes() {
    return [
      { value: "", label: "Not Specified" },
      { value: "Study", label: "Study" },
      { value: "Research", label: "Research" },
      { value: "Group Study", label: "Group Study" },
      { value: "Reading", label: "Reading" },
      { value: "Computer Use", label: "Computer Use" },
      { value: "Assignment Work", label: "Assignment Work" },
      { value: "Thesis/Dissertation", label: "Thesis/Dissertation" },
      { value: "Reference Material", label: "Reference Material" },
      { value: "Meeting", label: "Meeting" },
      { value: "Presentation Prep", label: "Presentation Prep" },
      { value: "Exam Preparation", label: "Exam Preparation" },
      { value: "Project Work", label: "Project Work" },
      { value: "Other", label: "Other" },
    ];
  }
  /**
   * Get available academic years
   */
  getAvailableYears() {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let i = currentYear - 10; i <= currentYear + 5; i++) {
      years.push({ value: i, label: String(i) });
    }
    return years;
  }
  /**
   * Filter attendance records based on user role and permissions
   */
  filterAttendanceByRole(attendanceRecords, userRole, currentUserId) {
    if (!Array.isArray(attendanceRecords)) return [];
    const role = userRole?.toLowerCase();
    // Admins and librarians can see all records
    if (["admin", "librarian"].includes(role)) {
      return attendanceRecords;
    }
    // Regular users can only see their own records
    return attendanceRecords.filter((record) => {
      return (
        record.libraryId === currentUserId || record.createdBy === currentUserId
      );
    });
  }
  /**
   * Sort attendance records
   */
  sortAttendance(attendanceRecords, sortBy = "newest") {
    if (!Array.isArray(attendanceRecords)) return [];
    return [...attendanceRecords].sort((a, b) => {
      switch (sortBy) {
        case "newest":
          return new Date(b.inTime || 0) - new Date(a.inTime || 0);
        case "oldest":
          return new Date(a.inTime || 0) - new Date(b.inTime || 0);
        case "library_id":
          return (a.libraryId || "").localeCompare(b.libraryId || "");
        case "user_name":
          return (a.userName || "").localeCompare(b.userName || "");
        case "status":
          // Active sessions first
          if (a.isActive && !b.isActive) return -1;
          if (!a.isActive && b.isActive) return 1;
          return 0;
        default:
          return 0;
      }
    });
  }
  /**
   * Check if user can perform operations based on role
   */
  canUserManageAttendance(currentUser) {
    if (!currentUser) return false;
    const userRoles = Array.isArray(currentUser.roles)
      ? currentUser.roles
      : [currentUser.roles];
    return userRoles.some((role) =>
      ["admin", "librarian"].includes(role?.toLowerCase())
    );
  }
  /**
   * Check if user can view attendance reports
   */
  canUserViewReports(currentUser) {
    if (!currentUser) return false;
    const userRoles = Array.isArray(currentUser.roles)
      ? currentUser.roles
      : [currentUser.roles];
    return userRoles.some((role) =>
      ["admin", "librarian"].includes(role?.toLowerCase())
    );
  }
  /**
   * Check if user can force checkout others
   */
  canUserForceCheckout(currentUser) {
    if (!currentUser) return false;
    const userRoles = Array.isArray(currentUser.roles)
      ? currentUser.roles
      : [currentUser.roles];
    return userRoles.some((role) =>
      ["admin", "librarian"].includes(role?.toLowerCase())
    );
  }
  /**
   * Format attendance record for display in UI components
   */
  formatForDisplay(attendance) {
    if (!attendance) return null;
    return {
      ...attendance,
      statusDisplay: this.getStatusDisplay(attendance.status),
      durationDisplay: attendance.duration?.formatted || "N/A",
      programDisplay: attendance.program || "Not specified",
      purposeOfVisitDisplay: attendance.purposeOfVisit || "Not specified",
      yearDisplay: attendance.year ? String(attendance.year) : "Not specified",
    };
  }
  /**
   * Generate attendance summary statistics
   */
  generateSummaryStats(attendanceRecords) {
    if (!Array.isArray(attendanceRecords)) {
      return {
        total: 0,
        active: 0,
        completed: 0,
        averageDuration: 0,
        totalHours: 0,
      };
    }
    const stats = {
      total: attendanceRecords.length,
      active: 0,
      completed: 0,
      totalHours: 0,
      completedSessions: [],
    };
    attendanceRecords.forEach((record) => {
      if (record.isActive) {
        stats.active++;
      } else {
        stats.completed++;
        if (record.duration?.hours) {
          stats.totalHours += record.duration.hours;
          stats.completedSessions.push(record.duration.hours);
        }
      }
    });
    // Calculate average duration for completed sessions
    stats.averageDuration =
      stats.completedSessions.length > 0
        ? stats.totalHours / stats.completedSessions.length
        : 0;
    return {
      ...stats,
      averageDurationFormatted: this._formatHours(stats.averageDuration),
      totalHoursFormatted: this._formatHours(stats.totalHours),
    };
  }
  /**
   * Format hours for display
   */
  _formatHours(hours) {
    if (!hours || hours < 0) return "0h 0m";
    const wholeHours = Math.floor(hours);
    const minutes = Math.round((hours - wholeHours) * 60);
    if (wholeHours > 0) {
      return `${wholeHours}h ${minutes}m`;
    } else {
      return `${minutes}m`;
    }
  }
  /**
   * Generate daily attendance report data
   */
  generateDailyReport(attendanceRecords, targetDate) {
    if (!Array.isArray(attendanceRecords)) return null;
    const date = targetDate ? new Date(targetDate) : new Date();
    const dateStr = date.toISOString().split("T")[0];
    // Filter records for the target date
    const dailyRecords = attendanceRecords.filter((record) => {
      if (!record.inTime) return false;
      const recordDate = new Date(record.inTime).toISOString().split("T")[0];
      return recordDate === dateStr;
    });
    const stats = this.generateSummaryStats(dailyRecords);
    // Program breakdown
    const programBreakdown = {};
    dailyRecords.forEach((record) => {
      const program = record.program || "Not specified";
      programBreakdown[program] = (programBreakdown[program] || 0) + 1;
    });
    // Purpose breakdown
    const purposeBreakdown = {};
    dailyRecords.forEach((record) => {
      const purpose = record.purposeOfVisit || "Not specified";
      purposeBreakdown[purpose] = (purposeBreakdown[purpose] || 0) + 1;
    });
    // Hourly breakdown (check-ins by hour)
    const hourlyBreakdown = {};
    dailyRecords.forEach((record) => {
      if (record.inTime) {
        const hour = new Date(record.inTime).getHours();
        const hourKey = `${hour}:00`;
        hourlyBreakdown[hourKey] = (hourlyBreakdown[hourKey] || 0) + 1;
      }
    });
    return {
      date: dateStr,
      dateDisplay: this._formatDateForDisplay(date.toISOString()),
      records: dailyRecords,
      stats,
      programBreakdown,
      purposeBreakdown,
      hourlyBreakdown,
      peakHour: this._findPeakHour(hourlyBreakdown),
    };
  }
  /**
   * Find peak hour from hourly breakdown
   */
  _findPeakHour(hourlyBreakdown) {
    if (!hourlyBreakdown || Object.keys(hourlyBreakdown).length === 0) {
      return null;
    }
    let peakHour = null;
    let maxCount = 0;
    Object.entries(hourlyBreakdown).forEach(([hour, count]) => {
      if (count > maxCount) {
        maxCount = count;
        peakHour = hour;
      }
    });
    return peakHour ? { hour: peakHour, count: maxCount } : null;
  }
  /**
   * Validate library ID format
   */
  validateLibraryId(libraryId) {
    if (!libraryId) return { isValid: false, error: "Library ID is required" };
    const id = String(libraryId).trim();
    // Basic validations
    if (id.length === 0) {
      return { isValid: false, error: "Library ID cannot be empty" };
    }
    if (id.length > 50) {
      return {
        isValid: false,
        error: "Library ID too long (max 50 characters)",
      };
    }
    // Check for special characters that might cause issues
    if (!/^[a-zA-Z0-9\-_.]+$/.test(id)) {
      return {
        isValid: false,
        error:
          "Library ID can only contain letters, numbers, hyphens, underscores, and periods",
      };
    }
    return { isValid: true };
  }
  /**
   * Export attendance data to CSV format
   */
  exportToCSV(attendanceRecords) {
    if (!Array.isArray(attendanceRecords) || attendanceRecords.length === 0) {
      return "";
    }
    const headers = [
      "Library ID",
      "User Name",
      "Program",
      "Year",
      "Purpose of Visit",
      "Check-in Time",
      "Check-out Time",
      "Duration (Hours)",
      "Status",
      "Date",
    ];
    const csvRows = [headers.join(",")];
    attendanceRecords.forEach((record) => {
      const row = [
        `"${record.libraryId || ""}"`,
        `"${record.userName || ""}"`,
        `"${record.program || ""}"`,
        `"${record.year || ""}"`,
        `"${record.purposeOfVisit || ""}"`,
        `"${record.inTimeDisplay || ""}"`,
        `"${record.outTimeDisplay || ""}"`,
        record.duration?.hours || "",
        `"${record.status || ""}"`,
        `"${record.dateDisplay || ""}"`,
      ];
      csvRows.push(row.join(","));
    });
    return csvRows.join("\n");
  }
  /**
   * Create attendance data for quick check-in
   */
  createQuickCheckInData(
    libraryId,
    userName,
    program = "",
    year = null,
    purposeOfVisit = ""
  ) {
    return {
      libraryId: String(libraryId).trim(),
      userName: String(userName).trim(),
      program: program ? String(program).trim() : "",
      year: year ? parseInt(year) : null,
      purposeOfVisit: purposeOfVisit ? String(purposeOfVisit).trim() : "",
    };
  }
  /**
   * Get time-based greeting for UI
   */
  getTimeBasedGreeting() {
    const hour = new Date().getHours();
    if (hour < 12) {
      return "Good morning";
    } else if (hour < 17) {
      return "Good afternoon";
    } else {
      return "Good evening";
    }
  }
  /**
   * Check if it's a reasonable time for library operations
   */
  isLibraryHours() {
    const now = new Date();
    const hour = now.getHours();
    // Assuming library hours are 6 AM to 11 PM
    return hour >= 6 && hour < 23;
  }
  /**
   * Generate helpful messages based on context
   */
  getContextualMessage(context) {
    const messages = {
      "check-in-success": "Welcome to the library! Have a productive session.",
      "check-out-success": "Thank you for visiting! Have a great day.",
      "already-checked-in":
        "You are already checked in. Use check-out when you leave.",
      "not-checked-in":
        "You are not currently checked in. Please check in first.",
      "outside-hours": "Please note: This is outside typical library hours.",
      "long-session":
        "You have been in the library for quite a while. Consider taking a break!",
      error: "Something went wrong. Please try again or contact support.",
    };
    return messages[context] || "";
  }
  /**
   * Filter attendance records by date range
   */
  filterByDateRange(records, startDate, endDate) {
    if (!Array.isArray(records)) return [];
    const start = startDate ? new Date(startDate) : null;
    const end = endDate ? new Date(endDate) : null;
    return records.filter((record) => {
      if (!record.inTime) return false;
      const recordDate = new Date(record.inTime);
      if (start && recordDate < start) return false;
      if (end && recordDate > end) return false;
      return true;
    });
  }
  /**
   * Filter by status (active/completed)
   */
  filterByStatus(records, status) {
    if (!Array.isArray(records)) return [];
    switch (status) {
      case "active":
        return records.filter((record) => record.isActive);
      case "completed":
        return records.filter((record) => !record.isActive);
      case "all":
      default:
        return records;
    }
  }
  /**
   * Filter by program/department
   */
  filterByProgram(records, program) {
    if (!Array.isArray(records) || !program) return records;
    return records.filter((record) => {
      const recordProgram = (record.program || "").toLowerCase();
      const searchProgram = program.toLowerCase();
      return recordProgram.includes(searchProgram);
    });
  }
  /**
   * Filter by purpose of visit
   */
  filterByPurpose(records, purpose) {
    if (!Array.isArray(records) || !purpose) return records;
    return records.filter((record) => {
      const recordPurpose = (record.purposeOfVisit || "").toLowerCase();
      const searchPurpose = purpose.toLowerCase();
      return recordPurpose.includes(searchPurpose);
    });
  }
  /**
   * Filter by library ID (partial match)
   */
  filterByLibraryId(records, libraryId) {
    if (!Array.isArray(records) || !libraryId) return records;
    const searchId = libraryId.toLowerCase();
    return records.filter((record) => {
      const recordId = (record.libraryId || "").toLowerCase();
      return recordId.includes(searchId);
    });
  }
  /**
   * Filter by user name (partial match)
   */
  filterByUserName(records, userName) {
    if (!Array.isArray(records) || !userName) return records;
    const searchName = userName.toLowerCase();
    return records.filter((record) => {
      const recordName = (record.userName || "").toLowerCase();
      return recordName.includes(searchName);
    });
  }
  /**
   * Filter by session duration
   */
  filterByDuration(records, minHours = 0, maxHours = null) {
    if (!Array.isArray(records)) return [];
    return records.filter((record) => {
      if (!record.duration?.hours) return false;
      const hours = record.duration.hours;
      if (hours < minHours) return false;
      if (maxHours !== null && hours > maxHours) return false;
      return true;
    });
  }
  /**
   * Multi-filter method - combine multiple filters
   */
  applyFilters(records, filters = {}) {
    if (!Array.isArray(records)) return [];
    let filteredRecords = [...records];
    // Apply each filter if specified
    if (filters.dateFrom || filters.dateTo) {
      filteredRecords = this.filterByDateRange(
        filteredRecords,
        filters.dateFrom,
        filters.dateTo
      );
    }
    if (filters.status) {
      filteredRecords = this.filterByStatus(filteredRecords, filters.status);
    }
    if (filters.program) {
      filteredRecords = this.filterByProgram(filteredRecords, filters.program);
    }
    if (filters.purposeOfVisit) {
      filteredRecords = this.filterByPurpose(
        filteredRecords,
        filters.purposeOfVisit
      );
    }
    if (filters.libraryId) {
      filteredRecords = this.filterByLibraryId(
        filteredRecords,
        filters.libraryId
      );
    }
    if (filters.userName) {
      filteredRecords = this.filterByUserName(
        filteredRecords,
        filters.userName
      );
    }
    if (filters.minDuration || filters.maxDuration) {
      filteredRecords = this.filterByDuration(
        filteredRecords,
        filters.minDuration,
        filters.maxDuration
      );
    }
    // Apply role-based filtering
    if (filters.userRole && filters.currentUserId) {
      filteredRecords = this.filterAttendanceByRole(
        filteredRecords,
        filters.userRole,
        filters.currentUserId
      );
    }
    // Apply sorting
    if (filters.sortBy) {
      filteredRecords = this.sortAttendance(filteredRecords, filters.sortBy);
    }
    return filteredRecords;
  }
  /**
   * Search records with text-based query
   */
  searchRecords(records, searchQuery) {
    if (!Array.isArray(records) || !searchQuery) return records;
    const query = searchQuery.toLowerCase();
    return records.filter((record) => {
      const searchableFields = [
        record.libraryId,
        record.userName,
        record.program,
        record.purposeOfVisit,
        String(record.year),
      ].filter(Boolean);
      return searchableFields.some((field) =>
        field.toLowerCase().includes(query)
      );
    });
  }
  /**
   * Get purpose of visit statistics
   */
  getPurposeStatistics(attendanceRecords) {
    if (!Array.isArray(attendanceRecords)) return {};

    const purposeStats = {};
    let totalWithPurpose = 0;

    attendanceRecords.forEach((record) => {
      const purpose = record.purposeOfVisit || "Not specified";
      purposeStats[purpose] = (purposeStats[purpose] || 0) + 1;
      if (record.purposeOfVisit) {
        totalWithPurpose++;
      }
    });

    // Calculate percentages
    const total = attendanceRecords.length;
    const statsWithPercentages = {};

    Object.entries(purposeStats).forEach(([purpose, count]) => {
      statsWithPercentages[purpose] = {
        count,
        percentage: total > 0 ? Math.round((count / total) * 100) : 0,
      };
    });

    return {
      breakdown: statsWithPercentages,
      totalRecords: total,
      recordsWithPurpose: totalWithPurpose,
      recordsWithoutPurpose: total - totalWithPurpose,
      completionRate:
        total > 0 ? Math.round((totalWithPurpose / total) * 100) : 0,
    };
  }
  /**
   * Generate purpose-based analytics
   */
  analyzePurposePatterns(attendanceRecords) {
    if (!Array.isArray(attendanceRecords)) return null;

    const patterns = {
      mostCommonPurpose: null,
      leastCommonPurpose: null,
      averageDurationByPurpose: {},
      purposesByTimeOfDay: {},
      weeklyPurposePatterns: {},
    };

    const purposeData = {};

    attendanceRecords.forEach((record) => {
      const purpose = record.purposeOfVisit || "Not specified";

      if (!purposeData[purpose]) {
        purposeData[purpose] = {
          count: 0,
          totalDuration: 0,
          durations: [],
          timeSlots: {},
          weekdays: {},
        };
      }

      purposeData[purpose].count++;

      // Duration analysis
      if (record.duration?.hours) {
        purposeData[purpose].totalDuration += record.duration.hours;
        purposeData[purpose].durations.push(record.duration.hours);
      }

      // Time of day analysis
      if (record.inTime) {
        const hour = new Date(record.inTime).getHours();
        const timeSlot = this._getTimeSlot(hour);
        purposeData[purpose].timeSlots[timeSlot] =
          (purposeData[purpose].timeSlots[timeSlot] || 0) + 1;

        // Weekday analysis
        const weekday = new Date(record.inTime).toLocaleDateString("en-US", {
          weekday: "long",
        });
        purposeData[purpose].weekdays[weekday] =
          (purposeData[purpose].weekdays[weekday] || 0) + 1;
      }
    });

    // Find most and least common purposes
    let maxCount = 0;
    let minCount = Infinity;

    Object.entries(purposeData).forEach(([purpose, data]) => {
      if (data.count > maxCount) {
        maxCount = data.count;
        patterns.mostCommonPurpose = { purpose, count: data.count };
      }
      if (data.count < minCount) {
        minCount = data.count;
        patterns.leastCommonPurpose = { purpose, count: data.count };
      }

      // Calculate average duration
      patterns.averageDurationByPurpose[purpose] = {
        averageHours:
          data.durations.length > 0
            ? data.totalDuration / data.durations.length
            : 0,
        totalSessions: data.durations.length,
        formatted: this._formatHours(
          data.durations.length > 0
            ? data.totalDuration / data.durations.length
            : 0
        ),
      };

      patterns.purposesByTimeOfDay[purpose] = data.timeSlots;
      patterns.weeklyPurposePatterns[purpose] = data.weekdays;
    });

    return patterns;
  }
  /**
   * Get time slot from hour (for analytics)
   */
  _getTimeSlot(hour) {
    if (hour >= 6 && hour < 12) return "Morning";
    if (hour >= 12 && hour < 17) return "Afternoon";
    if (hour >= 17 && hour < 21) return "Evening";
    return "Night";
  }
  /**
   * Generate comprehensive analytics report
   */
  generateAnalyticsReport(attendanceRecords, dateRange = null) {
    if (!Array.isArray(attendanceRecords)) return null;

    let records = attendanceRecords;

    // Filter by date range if provided
    if (dateRange?.startDate || dateRange?.endDate) {
      records = this.filterByDateRange(
        records,
        dateRange.startDate,
        dateRange.endDate
      );
    }

    const basicStats = this.generateSummaryStats(records);
    const purposeStats = this.getPurposeStatistics(records);
    const purposePatterns = this.analyzePurposePatterns(records);

    // Program breakdown
    const programStats = {};
    records.forEach((record) => {
      const program = record.program || "Not specified";
      programStats[program] = (programStats[program] || 0) + 1;
    });

    // Peak usage analysis
    const hourlyUsage = {};
    const dailyUsage = {};

    records.forEach((record) => {
      if (record.inTime) {
        const date = new Date(record.inTime);
        const hour = date.getHours();
        const day = date.toLocaleDateString();

        hourlyUsage[hour] = (hourlyUsage[hour] || 0) + 1;
        dailyUsage[day] = (dailyUsage[day] || 0) + 1;
      }
    });

    const peakHour = this._findPeakFromObject(hourlyUsage);
    const peakDay = this._findPeakFromObject(dailyUsage);

    return {
      dateRange: dateRange || { startDate: null, endDate: null },
      basicStats,
      purposeStats,
      purposePatterns,
      programStats,
      peakUsage: {
        hour: peakHour,
        day: peakDay,
      },
      hourlyUsage,
      dailyUsage,
      generatedAt: new Date().toISOString(),
    };
  }
  /**
   * Find peak from object (helper for analytics)
   */
  _findPeakFromObject(dataObj) {
    if (!dataObj || Object.keys(dataObj).length === 0) return null;

    let peakKey = null;
    let maxValue = 0;

    Object.entries(dataObj).forEach(([key, value]) => {
      if (value > maxValue) {
        maxValue = value;
        peakKey = key;
      }
    });

    return peakKey ? { key: peakKey, value: maxValue } : null;
  }
}

export const serializer = new AttendanceSerializer();
export default AttendanceSerializer;
